// OpenStreetMap Nominatim Geocoding Service
// Free geocoding API with 1 request per second rate limit

class GeocodingService {
    constructor() {
        this.baseUrl = 'https://nominatim.openstreetmap.org';
        this.lastRequestTime = 0;
        this.minRequestInterval = 1000; // 1 second between requests
        this.cache = new Map();
        this.maxCacheSize = 100;
    }

    // Rate limiting helper
    async waitForRateLimit() {
        const now = Date.now();
        const timeSinceLastRequest = now - this.lastRequestTime;

        if (timeSinceLastRequest < this.minRequestInterval) {
            const waitTime = this.minRequestInterval - timeSinceLastRequest;
            await new Promise(resolve => setTimeout(resolve, waitTime));
        }

        this.lastRequestTime = Date.now();
    }

    // Cache management
    getCached(query) {
        return this.cache.get(query.toLowerCase());
    }

    setCached(query, results) {
        const key = query.toLowerCase();

        // Simple LRU cache - remove oldest if at max size
        if (this.cache.size >= this.maxCacheSize) {
            const firstKey = this.cache.keys().next().value;
            this.cache.delete(firstKey);
        }

        this.cache.set(key, results);
    }

    // Search for locations
    async searchLocations(query) {
        if (!query || query.length < 2) {
            return [];
        }

        // Check cache first
        const cached = this.getCached(query);
        if (cached) {
            return cached;
        }

        try {
            // Respect rate limiting
            await this.waitForRateLimit();

            const params = new URLSearchParams({
                q: query,
                format: 'json',
                limit: '5',
                addressdetails: '1',
                extratags: '0',
                namedetails: '0'
            });

            const response = await fetch(`${this.baseUrl}/search?${params}`, {
                headers: {
                    'User-Agent': 'InterstellarMap-StarMap-App/1.0'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            const results = data.map(item => ({
                name: this.formatLocationName(item),
                lat: parseFloat(item.lat),
                lng: parseFloat(item.lon),
                displayName: item.display_name,
                type: item.type,
                importance: item.importance
            }));

            // Cache the results
            this.setCached(query, results);

            return results;

        } catch (error) {
            console.error('Nominatim geocoding error:', error);

            // Return empty array on error - component will handle fallback
            return [];
        }
    }

    // Format location name for better display
    formatLocationName(item) {
        const address = item.address || {};

        // Priority order for location components
        const components = [];

        // Add city/town/village
        if (address.city) components.push(address.city);
        else if (address.town) components.push(address.town);
        else if (address.village) components.push(address.village);
        else if (address.hamlet) components.push(address.hamlet);

        // Add state/province
        if (address.state) components.push(address.state);
        else if (address.province) components.push(address.province);

        // Add country
        if (address.country) components.push(address.country);

        // If we have components, use them; otherwise fall back to display_name
        if (components.length > 0) {
            return components.join(', ');
        }

        // Fallback: use display_name but try to shorten it
        const displayParts = item.display_name.split(', ');
        if (displayParts.length > 3) {
            return displayParts.slice(0, 3).join(', ');
        }

        return item.display_name;
    }

    // Reverse geocoding (coordinates to location name)
    async reverseGeocode(lat, lng) {
        try {
            await this.waitForRateLimit();

            const params = new URLSearchParams({
                lat: lat.toString(),
                lon: lng.toString(),
                format: 'json',
                addressdetails: '1'
            });

            const response = await fetch(`${this.baseUrl}/reverse?${params}`, {
                headers: {
                    'User-Agent': 'InterstellarMap-StarMap-App/1.0'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                name: this.formatLocationName(data),
                displayName: data.display_name
            };

        } catch (error) {
            console.error('Reverse geocoding error:', error);
            return null;
        }
    }

    // Clear cache (useful for testing or memory management)
    clearCache() {
        this.cache.clear();
    }
}

// Export singleton instance
export const geocodingService = new GeocodingService();

// Export search function for easy use
export const searchLocations = (query) => geocodingService.searchLocations(query);
export const reverseGeocode = (lat, lng) => geocodingService.reverseGeocode(lat, lng); 